import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Col, Row } from 'antd';
import clsx from 'clsx';
import { map } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../AppContext';
import { AddButton } from '../../../assets/svg';
import {
  BREAKPOINTS,
  DEFAULT_PAGE_SIZE,
  GA_EVENT,
  GA_LABEL,
} from '../../../common/constants';
import { Event } from '../../../common/trackEvents';
import CommonCard from '../../../components/CommonCard';
import CommonTable from '../../../components/CommonTable';
import EllipsisText from '../../../components/EllipsisText';
import InfiniteScrollHandler from '../../../components/InfiniteScrollHandler';
import Portal from '../../../components/Portal';
import SearchComponent from '../../../components/SearchComponent';
import ApproversListingModal from '../ApproversListingModal';
import DeleteAgencyModal from '../DeleteAgencyModal';
import { DELETE_AGENCY_USER } from '../graphql/Mutation';
import { GET_AGENCY_CONTACTS } from '../graphql/Queries';
import AddContactModal from './AddContactModal';

const AgencyContactTable = ({ agencyId }) => {
  const initialAgencyContactFilter = {
    skip: 0,
    limit: 10,
    agencyId: Number(agencyId) || 0,
    sortBy: { field: 'createdAt', order: 'DESC' },
  };

  const initialPaginationValue = {
    total: 0,
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  };
  const { getCurrentUser } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [deleteModal, setDeleteModal] = useState(false);
  const [sortedInfo, setSortedInfo] = useState({});
  const [mutationId, setMutationId] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [agencyContactData, setAgencyContactData] = useState();
  const [hasMore, setHasMore] = useState(true);
  const [scrollFlag, setScrollFlag] = useState(false);
  const [contactListData, setContactListData] = useState([]);
  const [showApproverDetailsModal, setShowApproverDetailsModal] =
    useState(false);
  const [agencyContactFilter, setAgencyContactFilter] = useState(
    initialAgencyContactFilter,
  );
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  const [fetchAgencyContactsData, { loading }] = useLazyQuery(
    GET_AGENCY_CONTACTS,
    {
      variables: { filter: agencyContactFilter },
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const data = res?.agencyUserList?.data || [];
        const pagination = {
          ...paginationProp,
          total: res?.agencyUserList?.total,
        };
        setPaginationProp(pagination);
        if (scrollFlag) {
          const datalist = [...contactListData, ...data];
          setContactListData(datalist);
          setScrollFlag(false);
        } else {
          const datalist = [...data];
          setContactListData(datalist);
        }
        setHasMore(!!data?.length);
      },
      onError() {},
    },
  );

  useEffect(() => {
    fetchAgencyContactsData({ variables: { filter: agencyContactFilter } });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleRefetchAfterDelete = () => {
    const newSkip =
      contactListData?.length === 1
        ? Math.max(0, agencyContactFilter?.skip - paginationProp?.pageSize)
        : agencyContactFilter?.skip;
    setAgencyContactFilter({
      ...agencyContactFilter,
      skip: newSkip,
      sortBy: { field: 'createdAt', order: 'DESC' },
    });
    fetchAgencyContactsData({
      variables: {
        filter: {
          ...agencyContactFilter,
          agencyId: Number(agencyId),
          skip: newSkip,
          sortBy: { field: 'createdAt', order: 'DESC' },
        },
      },
    });
  };
  const [deleteAgencyContact] = useMutation(DELETE_AGENCY_USER, {
    onCompleted(res) {
      Event(GA_EVENT.DELETE_GLOBAL_AGENCY_CONTACT, {
        label: GA_LABEL.DELETE_GLOBAL_AGENCY_CONTACT,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        agency_id: agencyId,
        contact_id: mutationId,
        user_id: currentUser?.id,
        user_name: currentUser?.name,
        tenant_id: currentUser?.tenantUser?.tenant?.id,
        tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
      });
      setDeleteModal(false);
      if (res?.deleteAgencyUser?.assignedAsApprover) {
        setShowApproverDetailsModal(true);
      } else {
        handleRefetchAfterDelete();
      }
    },
    onError() {},
  });

  useEffect(() => {
    const userFilterCopy = {
      ...agencyContactFilter,
      skip: 0,
      limit: 10,
      agencyId: Number(agencyId),
    };
    setPaginationProp(initialPaginationValue);
    fetchAgencyContactsData({ variables: { filter: userFilterCopy } });
    setAgencyContactFilter(userFilterCopy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyId]);

  const handleAddEditContact = () => {
    setShowModal(true);
  };
  const refetchAgencyContactDetails = () => {
    setSortedInfo({});
    setAgencyContactFilter(initialAgencyContactFilter);
    setPaginationProp(initialPaginationValue);
    fetchAgencyContactsData({
      variables: {
        filter: { ...initialAgencyContactFilter, agencyId: Number(agencyId) },
      },
    });
  };

  const handleTableChange = (pagination, filter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setSortedInfo(sorter);
    setPaginationProp({ ...paginationProp, ...pagination });
    if (sorter?.column) {
      setAgencyContactFilter({
        ...agencyContactFilter,
        skip,
        limit: pagination.pageSize,
        sortBy: {
          field: sorter.field,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      });
      fetchAgencyContactsData({
        variables: {
          filter: {
            ...agencyContactFilter,
            skip,
            limit: pagination.pageSize,
            sortBy: {
              field: sorter.field,
              order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
            },
          },
        },
      });
    } else {
      setAgencyContactFilter({
        ...agencyContactFilter,
        skip,
        limit: pagination.pageSize,
        sortBy: { field: 'createdAt', order: 'DESC' },
      });
      fetchAgencyContactsData({
        variables: {
          filter: {
            ...agencyContactFilter,
            skip,
            limit: pagination.pageSize,
            sortBy: { field: 'createdAt', order: 'DESC' },
          },
        },
      });
    }
  };

  //! commented for future use
  // const getActionBtn = (record) => {
  //   return (
  //     <div className="d-flex contact-action-button justify-end">
  //       <Button
  //         shape="round"
  //         icon={<EditButton />}
  //         className="edit-button pointer b-0"
  //         onClick={() => {
  //           setAgencyContactData(record);
  //           handleAddEditContact();
  //         }}
  //       />
  //       <Button
  //         className="delete-button pointer b-0 text-underline"
  //         type="link"
  //         onClick={() => {
  //           setTitle('Team User');
  //           setName(record?.name);
  //           setMutationId(record?.id);
  //           setDeleteModal(true);
  //         }}
  //       >
  //         Remove
  //       </Button>
  //     </div>
  //   );
  // };
  const columns = [
    {
      title: '#',
      key: 'id',
      width: '10%',
      render: (text, record, index) => {
        return <div>{agencyContactFilter?.skip + index + 1}</div>;
      },
    },
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: '15%',
      sortOrder: sortedInfo?.columnKey === 'name' && sortedInfo?.order,
      render: (text) => (
        <div>
          <EllipsisText className="text-break" text={text} />
        </div>
      ),
    },
    {
      title: 'MOBILE NUMBER',
      dataIndex: 'phoneNo',
      key: 'phoneNo',
      width: '15%',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'phoneNo' && sortedInfo?.order,
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      width: '25%',
      ellipsis: true,
      sortOrder: sortedInfo?.columnKey === 'email' && sortedInfo?.order,
      render: (text) => (
        <div>
          <EllipsisText className="text-break" text={text || '-'} />
        </div>
      ),
    },
    //! commented for future use
    // {
    //   key: 'action',
    //   width: '20%',
    //   align: 'right',
    //   // render: (record) => getActionBtn(record),
    // },
  ];

  const onSearchChange = async (value) => {
    setAgencyContactFilter({ ...agencyContactFilter, skip: 0, search: value });
    setPaginationProp(initialPaginationValue);
    fetchAgencyContactsData({
      variables: { filter: { ...agencyContactFilter, skip: 0, search: value } },
    });
  };
  const handleRefetch = () => {
    fetchAgencyContactsData({
      variables: {
        filter: {
          ...agencyContactFilter,
          skip: contactListData?.length,
          limit: DEFAULT_PAGE_SIZE,
        },
      },
    });
  };
  return (
    <>
      {showModal && (
        <AddContactModal
          showModal={showModal}
          setShowModal={setShowModal}
          agencyContactData={agencyContactData}
          isUpdate={!!agencyContactData}
          setAgencyContactData={setAgencyContactData}
          agencyId={Number(agencyId)}
          refetchAgencyContactDetails={refetchAgencyContactDetails}
        />
      )}
      {deleteModal && (
        <DeleteAgencyModal
          showModal={deleteModal}
          setShowModal={setDeleteModal}
          deleteMutation={deleteAgencyContact}
          mutationId={mutationId}
          setMutationId={setMutationId}
          agencyId={agencyId}
        />
      )}
      <Portal portalId="team-user-sub-header">
        <h5>
          {paginationProp?.total} Team
          {paginationProp?.total > 1 ? ' Users' : ' User'}
        </h5>
      </Portal>
      <Portal portalId="add-btn-contact">
        {!isDesktopViewport && (
          <SearchComponent
            id="search-container-id"
            getData={onSearchChange}
            className="search-component mr-10"
          />
        )}
        <div className="d-flex">
          <Button
            shape="round"
            type="primary"
            id="add-btn"
            className="add-button d-flex align-center"
            onClick={handleAddEditContact}
            icon={<AddButton width={26} />}
            disabled={!agencyId}
          >
            {isDesktopViewport && 'Add'}
          </Button>
        </div>
      </Portal>
      <div
        className={clsx('d-flex align-center width-percent-100 justify-end')}
      >
        {isDesktopViewport && (
          <SearchComponent
            id="search-container-id"
            className="search-component mb-10 mr-10 width-percent-50"
            getData={onSearchChange}
          />
        )}
      </div>
      {isDesktopViewport ? (
        <div className="table">
          <CommonTable
            rowClassName={(record) => {
              if (!record?.isActive) return 'deactivated-color';
            }}
            loadingData={loading}
            columns={columns}
            data={contactListData || []}
            onChange={handleTableChange}
            paginationConfig={paginationProp}
            rowKey={(obj) => obj?.id}
          />
        </div>
      ) : (
        <InfiniteScrollHandler
          scrollFlag={scrollFlag}
          loading={loading}
          refetchData={handleRefetch}
          setScrollFlag={setScrollFlag}
          hasMore={hasMore}
          wrapperClassName="contact-scroll-wrapper mt-5"
          dataLength={contactListData?.length}
          skeletonRows={columns?.length - 2}
        >
          <div id="sortable-container-div" />
          {map(contactListData, (item, index) => {
            return (
              <CommonCard key={item?.id} className="contact-card">
                <div className="common-card d-flex">
                  <div className="mr-5 fw-medium">{index + 1}.</div>
                  <div>
                    <div className="card-header fw-medium">
                      <Row wrap={false}>
                        <Col flex="auto">{item?.name}</Col>
                      </Row>
                    </div>
                    <div className="card-content text-secondary">
                      <br />
                      <div className="mb-10 d-flex align-center">
                        <EllipsisText
                          className="text-secondary text-break mr-5"
                          text={item?.email}
                        />
                      </div>
                      <div className=" d-flex align-center">
                        <span className=" mr-5">{item?.phoneNo}</span>
                      </div>
                    </div>
                  </div>
                  {/*! commented for future use
                  <span className="position-absolute contact-action-btn">
                    {getActionBtn(item)}
                  </span> */}
                </div>
              </CommonCard>
            );
          })}
        </InfiniteScrollHandler>
      )}
      {showApproverDetailsModal && (
        <ApproversListingModal
          showModal={showApproverDetailsModal}
          setShowModal={setShowApproverDetailsModal}
          mutationId={mutationId}
          setMutationId={setMutationId}
          agencyId={agencyId}
        />
      )}
    </>
  );
};

export default AgencyContactTable;
